import React, { useCallback, useEffect, useRef } from 'react'
import ApexCharts from 'apexcharts'
import { useThemeMode } from '../../../_metronic/partials'
import { getCSSVariableValue } from '../../../_metronic/assets/ts/_utils'
import { IconPaths, KTSVG } from '_metronic/helpers'

export type SerieIndicador = {
  categoria: string
  media: number
  mediana: number
}

type Props = {
  /** Uma barra por indicador com dado; indicador sem dado não entra na série. */
  dados: SerieIndicador[]
  /** Todos os textos chegam por prop — o componente não tem literal de UI. */
  rotuloMedia: string
  rotuloMediana: string
  textoSemDados: string
  /** Formata o valor para o rótulo da barra, o tooltip e o texto alternativo. */
  formatarValor: (valor: number) => string
  idGrafico?: string
}

/**
 * Comparativo de média × mediana dos indicadores agregados do período, em barras
 * horizontais: o nome do indicador fica inteiro à esquerda e o valor, já
 * formatado, na ponta de cada barra — então barra curta (indicadores de minutos
 * ao lado de outros de dias) continua legível pelo número.
 * O contrato da D4a não expõe série temporal, então a tela não a simula.
 * Cores vêm de variável CSS (white-label), nunca de HEX da paleta, e a
 * distinção entre as séries tem legenda textual — cor não carrega significado
 * sozinha.
 */
const LARGURA_LINHA_ROTULO = 28

/** Quebra o nome do indicador em linhas curtas; a ApexCharts desenha um array como multilinha. */
const quebrarRotulo = (rotulo: string): string[] =>
  rotulo.split(' — ').flatMap((parte) =>
    parte.split(' ').reduce<string[]>((linhas, palavra) => {
      const ultima = linhas[linhas.length - 1]
      if (ultima && `${ultima} ${palavra}`.length <= LARGURA_LINHA_ROTULO) {
        linhas[linhas.length - 1] = `${ultima} ${palavra}`
      } else {
        linhas.push(palavra)
      }
      return linhas
    }, [])
  )

const IndicadoresBarChart: React.FC<Props> = ({
  dados,
  rotuloMedia,
  rotuloMediana,
  textoSemDados,
  formatarValor,
  idGrafico = 'kt_charts_indicadores_bar',
}) => {
  const chartRef = useRef<HTMLDivElement | null>(null)
  const { mode } = useThemeMode()

  const refreshChart = useCallback(() => {
    if (!chartRef.current) return

    // A mediana é o valor em destaque do painel: leva a cor primária.
    const corMediana = getCSSVariableValue('--bs-primary')
    const corMedia = getCSSVariableValue('--bs-gray-600')
    const corTexto = getCSSVariableValue('--bs-gray-700')
    const vazio = dados.length === 0

    const chart = new ApexCharts(chartRef.current, {
      chart: {
        type: 'bar',
        height: Math.max(220, dados.length * 72 + 60),
        toolbar: { show: false },
        fontFamily: 'inherit',
      },
      series: vazio
        ? []
        : [
            { name: rotuloMediana, data: dados.map((d) => d.mediana) },
            { name: rotuloMedia, data: dados.map((d) => d.media) },
          ],
      xaxis: {
        // Nome longo quebra em linhas (no " — " e por largura), em vez de ser cortado.
        categories: dados.map((d) => quebrarRotulo(d.categoria)),
        // Com o valor escrito na barra, a régua numérica só competiria com ele.
        labels: { show: false },
        axisTicks: { show: false },
        axisBorder: { show: false },
      },
      yaxis: {
        labels: { maxWidth: 260, style: { colors: corTexto, fontSize: '12px' } },
      },
      grid: { xaxis: { lines: { show: false } }, yaxis: { lines: { show: false } } },
      colors: [corMediana, corMedia],
      plotOptions: {
        bar: {
          horizontal: true,
          barHeight: '70%',
          borderRadius: 4,
          dataLabels: { position: 'top' },
        },
      },
      dataLabels: {
        enabled: true,
        formatter: (valor: number) => formatarValor(valor),
        offsetX: 36,
        textAnchor: 'start',
        style: { colors: [corTexto], fontSize: '12px', fontWeight: 600 },
      },
      legend: {
        show: true,
        position: 'bottom',
        labels: { colors: corTexto },
      },
      tooltip: {
        y: { formatter: (valor: number) => formatarValor(valor) },
      },
      stroke: { show: false },
      noData: {
        text: textoSemDados,
        align: 'center',
        verticalAlign: 'middle',
        style: { color: corTexto, fontSize: '14px' },
      },
    })

    chart.render()

    return chart
  }, [dados, rotuloMedia, rotuloMediana, formatarValor, textoSemDados])

  useEffect(() => {
    const chart = refreshChart()

    return () => {
      if (chart) {
        chart.destroy()
      }
    }
  }, [mode, refreshChart])

  // Sem série, o SVG vazio desenharia uma régua sem sentido: o estado vai em texto.
  if (dados.length === 0) {
    return (
      <p className='d-flex align-items-center text-gray-700 fs-6 mb-0'>
        <KTSVG path={IconPaths.CircleMinus} className='svg-icon-em me-2 fs-5' ariaHidden />
        <span>{textoSemDados}</span>
      </p>
    )
  }

  return (
    <>
      <div ref={chartRef} id={idGrafico} aria-hidden='true' />
      {/* O SVG da ApexCharts não é legível por leitor de tela: os mesmos números, em texto. */}
      <ul className='visually-hidden'>
        {dados.map((d) => (
          <li key={d.categoria}>
            {`${d.categoria}: ${rotuloMediana} ${formatarValor(
              d.mediana
            )}, ${rotuloMedia} ${formatarValor(d.media)}`}
          </li>
        ))}
      </ul>
    </>
  )
}

export { IndicadoresBarChart }
